import React, { useState, useContext, useMemo } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import 'dayjs/locale/ja'
import { AccountContext } from '@/contexts/account'
import { UnsavedChangesRouteGuard } from '@/components/molecules/unsaved-changes-route-guard'
import { useReservationEditingSections } from '@/hooks/use-reservation-editing-sections'
import {
  wholeContainerStyle,
  mainColumnStyle,
  checkinDetailContainerStyle,
  reservationDetailContainerStyle,
  leftContainerStyleForReservation,
  rightContainerStyleForReservation,
} from '@/constants/layout'
import * as api from '@/apis/aipass'
import { setHasRepeatPlugin, setHasSalesManagerPlugin, hasSpaceVisibility } from '@/libs/plugins'
import { useHandleDriversLicenseAuthRedirect } from '@/libs/drivers-license-auth-redirect'
import { LoadingFull } from '@/components/molecules/loading-full'
import { Header } from '@/components/organisms/header'
import { SideMenu } from '@/components/organisms/side-menu'
import { PersonInfo } from '@/components/organisms/customer/person-info'
import { AddGuestInfoModal } from '@/components/organisms/accommodation-management/add-guest-info-modal'
import { Sales } from '@/components/organisms/customer/sales-manager/sales'
import { CustomerLink } from '@/components/organisms/accommodation-management/customer-link'
import { SearchCustomerLinkButton } from '@/components/organisms/accommodation-management/search-customer-link-button'
import { SearchCustomerLinkModal } from '@/components/organisms/search-customer-link-modal'
import { SuggestCustomer } from '@/components/organisms/accommodation-management/suggest-customer'
import { ReservationList } from '@/components/organisms/customer/_reservation-list/reservation-list'
import { PlanInfoDetail } from '@/components/organisms/customer/_plan-info/plan-info-detail'
import { GuestRoom } from '@/components/organisms/customer/guest-room'
import { DetailEditInfoForCheckin } from '@/components/organisms/reservation/detail-edit-info-for-checkin'
import { ReservationDetailMemo } from '@/components/organisms/reservation/reservation-detail-memo'
import { ApprovedStatusType } from '@/models/reservation-approved-status'
import { ListSpaceForReservation } from '@/components/organisms/customer/list-space-for-reservation'
import { useBrowserUnloadGuard } from '@/hooks/use-browser-unload-guard'
import { useAdminCheckinDetail } from '@/hooks/use-admin-checkin-detail'

export const CheckinDetail: React.FC = () => {
  const { t } = useTranslation()
  const { plugins } = useContext<any>(AccountContext)
  const history = useHistory()
  const params = useParams<any>()
  const {
    checkin: customer,
    reservations,
    currentTabReservationId,
    setCurrentTabReservationId,
    customerLink: customerlink,
    tags,
    setTags,
    suggestedCustomers: customers,
    isFetching,
    isSavingCustomerLink,
    fetchAdminCheckin,
    linkCustomer,
    unlinkCustomer,
  } = useAdminCheckinDetail(params.id, 'checkinRead')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isAddGuestInfoModalOpen, setIsAddGuestInfoModalOpen] = useState<boolean>(false)
  const [isEdit, setIsEdit] = useState<boolean>(false)
  const [salesReloadKey, setSalesReloadKey] = useState<number>(0)

  // Customer management related (no repeat management plugin/plugin)
  // Display of customer search modal (before linking)
  const [isOpenSearchCustomerLinkModal, setIsOpenSearchCustomerLinkModal] = useState<boolean>(false)
  // Editing tags (after binding)
  const [isEditTag, setIsEditTag] = useState<boolean>(false)

  // Whether it is a sales management plugin
  const hasSalesManagerPlugin = setHasSalesManagerPlugin(plugins)

  // Whether it is a repeat management plugin
  const hasRepeatPlugin = setHasRepeatPlugin(plugins)
  const shouldShowSpace = useMemo(() => hasSpaceVisibility(plugins), [plugins])

  const checkinReception = async () => {
    const reception = await api.updateCheckinReception([params.id], ApprovedStatusType.Stay, 'accommodationDetail')
    if (!reception) return
    api.updateAdminCheckin({ checkinId: [params.id], approvedStatus: ApprovedStatusType.Stay }).then(() => {
      fetchAdminCheckin()
    })
  }

  const onChangeReservationStatus = () => {
    fetchAdminCheckin()
    setSalesReloadKey(v => v + 1)
  }

  // Customer linking (customer management/modal search)
  const onSaveCustomerLink = async (hotelCuicinUserId: string) => {
    if (await linkCustomer(hotelCuicinUserId)) setIsOpenSearchCustomerLinkModal(false)
  }

  const onClickCancelTag = () => {
    setIsEditTag(false)
    setTags(customerlink?.tagInfo?.tags?.map(tag => tag.id) || [])
  }

  const selectedReservation = useMemo(() => {
    return reservations?.find(r => r.reservationId === currentTabReservationId)
  }, [reservations, currentTabReservationId])

  // 編集されているセクションがあるか、そのセクション名
  const { hasUnsaved, unsavedLabels } = useReservationEditingSections()

  // ブラウザバックやリロード、別タブへの移動などの前に確認ダイアログを出す
  useBrowserUnloadGuard(hasUnsaved)

  // Redirect processing from license display authentication
  useHandleDriversLicenseAuthRedirect()

  return (
    <div css={wholeContainerStyle}>
      <SideMenu />
      <div className="mainColumn" css={mainColumnStyle}>
        <Header isBack title={t('Reservation Details')} goBack={history.goBack} />
        <div className="mainContainer" style={{ padding: 24 }}>
          {!!customer && (
            <>
              <div css={checkinDetailContainerStyle}>
                {/* Guest information */}
                <div css={leftContainerStyleForReservation}>
                  <PersonInfo checkin={customer} onClickAddGuestInfo={() => setIsAddGuestInfoModalOpen(true)} />
                </div>
                {/* Related customers */}
                <div css={rightContainerStyleForReservation}>
                  {/* No repeat management plugin */}
                  {!hasRepeatPlugin &&
                    (customerlink ? (
                      <CustomerLink
                        history={history}
                        customer={customerlink}
                        deleteCustomerLink={unlinkCustomer}
                        isEdit={isEditTag}
                        currentTags={tags}
                        onCancel={onClickCancelTag}
                      />
                    ) : (
                      <SearchCustomerLinkButton setIsOpenModal={setIsOpenSearchCustomerLinkModal} />
                    ))}
                  {/* Repeat management plugin available */}
                  {hasRepeatPlugin &&
                    (customerlink ? (
                      <CustomerLink
                        history={history}
                        customer={customerlink}
                        deleteCustomerLink={unlinkCustomer}
                        isEdit={isEditTag}
                        currentTags={tags}
                        onCancel={onClickCancelTag}
                      />
                    ) : (
                      <SuggestCustomer
                        customers={customers}
                        setIsOpenModal={setIsOpenSearchCustomerLinkModal}
                        onSaveCustomerLink={onSaveCustomerLink}
                      />
                    ))}
                </div>
              </div>
              <div css={reservationDetailContainerStyle}>
                <div className="default-block">
                  <ReservationList
                    reservations={reservations}
                    checkinReception={checkinReception}
                    currentTabReservationId={currentTabReservationId}
                    setCurrentTabReservationId={setCurrentTabReservationId}
                    frontSupportedForCheckin={customer.frontSupported}
                    checkinId={params.id}
                    onReload={() => {
                      setSalesReloadKey(v => v + 1)
                      fetchAdminCheckin()
                    }}
                  />
                </div>

                {reservations && selectedReservation && (
                  <>
                    <div className="default-block -multi-info">
                      <div className="reservation-info-block">
                        <PlanInfoDetail
                          reservation={selectedReservation}
                          onChangeReservation={fetchAdminCheckin}
                          isEdit={isEdit}
                          setIsEdit={setIsEdit}
                          onChangeReservationStatus={onChangeReservationStatus}
                        />
                      </div>
                      {isEdit && (
                        <DetailEditInfoForCheckin
                          reservation={selectedReservation}
                          isEdit={isEdit}
                          setIsEdit={setIsEdit}
                          onSaved={fetchAdminCheckin}
                        />
                      )}
                      <div className="reservation-memo-block">
                        <ReservationDetailMemo
                          reservationId={selectedReservation.reservationId}
                          checkoutDate={selectedReservation.checkoutDate}
                          customer={customerlink}
                          checkinId={customer.id}
                        />
                      </div>
                    </div>
                    <div className="default-block">
                      <GuestRoom reservation={selectedReservation} setIsLoading={setIsLoading} onSavedData={fetchAdminCheckin} />
                    </div>
                    {shouldShowSpace && (
                      <ListSpaceForReservation
                        reservationId={selectedReservation.reservationId}
                        checkinDate={selectedReservation.checkinDate}
                        approvedStatus={selectedReservation.approvedStatus}
                      />
                    )}
                  </>
                )}
              </div>
            </>
          )}
          {isOpenSearchCustomerLinkModal && (
            <SearchCustomerLinkModal onClose={() => setIsOpenSearchCustomerLinkModal(false)} onSaveCustomerLink={onSaveCustomerLink} />
          )}
          {isAddGuestInfoModalOpen && (
            <AddGuestInfoModal
              customer={customer}
              isCustomerLinked={!!customerlink?.id}
              onCancel={() => setIsAddGuestInfoModalOpen(false)}
              onSave={() => {
                fetchAdminCheckin()
                setIsAddGuestInfoModalOpen(false)
              }}
            />
          )}
          {/* Check-in Details_Usage Details */}
          {hasSalesManagerPlugin && !!reservations?.length && currentTabReservationId && (
            <Sales
              currentTabReservationId={currentTabReservationId}
              customer={customer}
              customerLink={customerlink}
              checkinId={params.id}
              reservations={reservations}
              reloadKey={salesReloadKey}
              onUpdateSales={fetchAdminCheckin}
            />
          )}
        </div>
      </div>
      <LoadingFull isLoading={isLoading || isFetching || isSavingCustomerLink} />
      <UnsavedChangesRouteGuard hasUnsaved={hasUnsaved} unsavedLabels={unsavedLabels} />
    </div>
  )
}
